import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, Save, DollarSign, Check } from "lucide-react";

const empty = () => ({
  id: null, title: "", tagline: "", category: "Beginner", level: "Beginner",
  duration_hrs: 1, price: 0, featured: false, cover_image: "",
  modules: [{ id: "m1", title: "Module 1", lessons: [{ id: "l1", title: "Lesson 1", duration: "10:00", audio_url: "", body_md: "" }] }],
});

export default function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [draft, setDraft] = useState(null);
  const [priceEdit, setPriceEdit] = useState({});   // { [courseId]: {value, saving} }

  const load = () => api.get("/courses").then((r) => setCourses(r.data));
  useEffect(() => { load(); }, []);

  const savePriceOnly = async (course) => {
    const raw = priceEdit[course.id]?.value;
    const newPrice = Number(raw);
    if (!Number.isFinite(newPrice) || newPrice < 0) {
      toast.error("Enter a valid price.");
      return;
    }
    if (newPrice === course.price) {
      setPriceEdit((p) => ({ ...p, [course.id]: null }));
      return;
    }
    setPriceEdit((p) => ({ ...p, [course.id]: { ...p[course.id], saving: true } }));
    try {
      await api.post("/admin/courses", { ...course, price: newPrice });
      toast.success(`Price updated · $${newPrice}`);
      setPriceEdit((p) => ({ ...p, [course.id]: null }));
      load();
    } catch {
      toast.error("Price update failed.");
      setPriceEdit((p) => ({ ...p, [course.id]: { ...p[course.id], saving: false } }));
    }
  };

  const save = async () => {
    try {
      const payload = { ...draft };
      await api.post("/admin/courses", payload);
      toast.success("Course saved.");
      setDraft(null);
      load();
    } catch (e) {
      toast.error("Save failed.");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this course?")) return;
    await api.delete(`/admin/courses/${id}`);
    toast.success("Deleted.");
    load();
  };

  const addModule = () => setDraft({ ...draft, modules: [...draft.modules, { id: `m${draft.modules.length + 1}`, title: "New module", lessons: [{ id: "l1", title: "Lesson 1", duration: "10:00", audio_url: "", body_md: "" }] }] });
  const addLesson = (mi) => {
    const m = draft.modules.map((mm, idx) => idx !== mi ? mm : { ...mm, lessons: [...mm.lessons, { id: `l${mm.lessons.length + 1}`, title: "New lesson", duration: "10:00", audio_url: "", body_md: "" }] });
    setDraft({ ...draft, modules: m });
  };
  const removeLesson = (mi, li) => {
    const m = draft.modules.map((mm, idx) => idx !== mi ? mm : { ...mm, lessons: mm.lessons.filter((_, j) => j !== li) });
    setDraft({ ...draft, modules: m });
  };
  const removeModule = (mi) => setDraft({ ...draft, modules: draft.modules.filter((_, j) => j !== mi) });

  return (
    <div className="text-white">
      <div className="flex justify-between items-end mb-8">
        <div>
          <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">Courses</div>
          <h1 className="font-display text-4xl">Manage the curriculum.</h1>
        </div>
        <Button onClick={() => setDraft(empty())} data-testid="admin-new-course" className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white">
          <Plus className="w-4 h-4 mr-2" /> New course
        </Button>
      </div>

      <div className="rounded-3xl bg-white/5 border border-white/10 divide-y divide-white/5">
        {courses.map((c) => {
          const editing = priceEdit[c.id];
          return (
            <div key={c.id} className="p-5 flex items-center gap-4" data-testid={`admin-course-row-${c.id}`}>
              {c.cover_image && <img src={c.cover_image} className="w-16 h-16 rounded-xl object-cover" alt="" />}
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg truncate">{c.title}</div>
                <div className="text-sm text-white/60">{c.category} · {c.chapters_count} lessons</div>
              </div>

              {/* Inline price editor */}
              <div className="flex items-center gap-2">
                {editing ? (
                  <>
                    <div className="flex items-center bg-white/5 border border-brand-pink/60 rounded-full h-10 pl-3 pr-1 gap-1">
                      <DollarSign className="w-3.5 h-3.5 text-brand-pink" />
                      <input
                        type="number"
                        min="0"
                        step="1"
                        autoFocus
                        data-testid={`admin-price-input-${c.id}`}
                        value={editing.value}
                        onChange={(e) => setPriceEdit((p) => ({ ...p, [c.id]: { ...p[c.id], value: e.target.value } }))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") savePriceOnly(c);
                          if (e.key === "Escape") setPriceEdit((p) => ({ ...p, [c.id]: null }));
                        }}
                        className="w-20 bg-transparent text-white text-sm outline-none"
                      />
                      <Button
                        size="sm"
                        onClick={() => savePriceOnly(c)}
                        disabled={editing.saving}
                        data-testid={`admin-price-save-${c.id}`}
                        className="h-8 w-8 p-0 rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <button
                      onClick={() => setPriceEdit((p) => ({ ...p, [c.id]: null }))}
                      className="text-white/50 hover:text-white text-xs"
                      data-testid={`admin-price-cancel-${c.id}`}
                    >
                      cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setPriceEdit((p) => ({ ...p, [c.id]: { value: String(c.price || 0), saving: false } }))}
                    data-testid={`admin-price-edit-${c.id}`}
                    className="group flex items-center gap-2 bg-brand-pink/10 hover:bg-brand-pink/20 border border-brand-pink/30 rounded-full h-10 px-4 transition"
                    title="Click to edit price"
                  >
                    <span className="font-display text-lg text-brand-pink">${c.price}</span>
                    <Edit3 className="w-3 h-3 text-brand-pink opacity-0 group-hover:opacity-100 transition" />
                  </button>
                )}
              </div>

              <Button onClick={() => setDraft(c)} data-testid={`admin-edit-${c.id}`} variant="ghost" className="text-white hover:bg-white/10 rounded-full" title="Full editor"><Edit3 className="w-4 h-4" /></Button>
              <Button onClick={() => del(c.id)} data-testid={`admin-del-${c.id}`} variant="ghost" className="text-brand-pink hover:bg-brand-pink/10 rounded-full"><Trash2 className="w-4 h-4" /></Button>
            </div>
          );
        })}
        {courses.length === 0 && <div className="p-10 text-white/50 text-center">No courses yet.</div>}
      </div>

      {draft && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur overflow-y-auto py-10 px-4">
          <div className="max-w-3xl mx-auto rounded-3xl bg-neutral-900 border border-white/10 p-8" data-testid="admin-course-editor">
            <div className="flex justify-between mb-6">
              <div className="font-display text-2xl">{draft.id ? "Edit course" : "New course"}</div>
              <button onClick={() => setDraft(null)} className="text-white/60 hover:text-white" data-testid="admin-editor-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                ["Title", "title", "text"], ["Tagline", "tagline", "text"],
                ["Category", "category", "text"], ["Level", "level", "text"],
                ["Duration (hrs)", "duration_hrs", "number"], ["Price ($)", "price", "number"],
                ["Cover image URL", "cover_image", "text"],
              ].map(([lbl, key, type]) => (
                <div key={key} className={key === "cover_image" ? "md:col-span-2" : ""}>
                  <Label className="mb-2 block text-white/80 text-xs uppercase tracking-widest">{lbl}</Label>
                  <Input
                    data-testid={`admin-course-${key}`}
                    value={draft[key] ?? ""}
                    type={type}
                    onChange={(e) => setDraft({ ...draft, [key]: type === "number" ? Number(e.target.value || 0) : e.target.value })}
                    className="rounded-full h-11 bg-white/5 border-white/10 text-white"
                  />
                </div>
              ))}
              <label className="flex items-center gap-2 text-sm text-white/80 md:col-span-2">
                <input type="checkbox" checked={!!draft.featured} onChange={(e) => setDraft({ ...draft, featured: e.target.checked })} /> Featured
              </label>
            </div>

            <div className="mt-8 space-y-4">
              {draft.modules.map((m, mi) => (
                <div key={mi} className="rounded-2xl bg-white/5 border border-white/10 p-4">
                  <div className="flex gap-2 items-center mb-3">
                    <Input value={m.title} onChange={(e) => { const ms = [...draft.modules]; ms[mi] = { ...m, title: e.target.value }; setDraft({ ...draft, modules: ms }); }} className="rounded-full bg-white/5 border-white/10 text-white" data-testid={`admin-mod-title-${mi}`} />
                    <Button variant="ghost" onClick={() => removeModule(mi)} className="text-brand-pink"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                  <div className="space-y-3">
                    {m.lessons.map((l, li) => {
                      const update = (patch) => {
                        const ms = [...draft.modules];
                        ms[mi].lessons[li] = { ...l, ...patch };
                        setDraft({ ...draft, modules: ms });
                      };
                      return (
                        <div key={li} className="rounded-2xl bg-black/40 border border-white/5 p-3 space-y-2" data-testid={`admin-lesson-${mi}-${li}`}>
                          <div className="grid grid-cols-12 gap-2 items-center">
                            <Input placeholder="Lesson title" className="col-span-6 rounded-full bg-white/5 border-white/10 text-white" value={l.title} onChange={(e) => update({ title: e.target.value })} />
                            <Input placeholder="Duration" className="col-span-2 rounded-full bg-white/5 border-white/10 text-white" value={l.duration} onChange={(e) => update({ duration: e.target.value })} />
                            <Input placeholder="Read min" type="number" className="col-span-3 rounded-full bg-white/5 border-white/10 text-white" value={l.read_minutes || ""} onChange={(e) => update({ read_minutes: Number(e.target.value || 0) })} />
                            <Button variant="ghost" onClick={() => removeLesson(mi, li)} className="col-span-1 text-brand-pink"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                          <div className="grid grid-cols-1 gap-2">
                            <Input placeholder="Audio URL (HeyGen MP3 / M4A)" className="rounded-full bg-white/5 border-white/10 text-white" value={l.audio_url || ""} onChange={(e) => update({ audio_url: e.target.value })} />
                          </div>
                          <Textarea placeholder="Written lesson (Markdown: ## headings, - lists, > quote, **bold**)" rows={5} className="rounded-2xl bg-white/5 border-white/10 text-white" value={l.body_md || ""} onChange={(e) => update({ body_md: e.target.value })} />
                          <div className="grid grid-cols-2 gap-2">
                            <Input placeholder="Lesson summary" className="rounded-full bg-white/5 border-white/10 text-white" value={l.summary || ""} onChange={(e) => update({ summary: e.target.value })} />
                            <Input placeholder="Host Hustle Action Step" className="rounded-full bg-white/5 border-white/10 text-white" value={l.action_step || ""} onChange={(e) => update({ action_step: e.target.value })} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Button onClick={() => addLesson(mi)} variant="ghost" size="sm" className="mt-3 text-white/80"><Plus className="w-4 h-4 mr-1" /> Add lesson</Button>
                </div>
              ))}
              <Button onClick={addModule} variant="outline" className="rounded-full border-white/20 bg-transparent text-white"><Plus className="w-4 h-4 mr-2" /> Add module</Button>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <Button variant="ghost" onClick={() => setDraft(null)} className="text-white/70">Cancel</Button>
              <Button onClick={save} data-testid="admin-save-course" className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white"><Save className="w-4 h-4 mr-2" /> Save course</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
